import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore"; // Importar funciones de Firestore
import { db } from "../../../firebase/firebaseConfig"; // Importar la configuración de Firebase
import { IconButton, Slider, Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, CircularProgress } from "@mui/material";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from "@mui/icons-material/Pause";

const ListaCancionesWeb = () => {
    const [audios, setAudios] = useState([]); // Estado para almacenar los audios
    const [loading, setLoading] = useState(true); // Estado para manejar la carga
    const [progresses, setProgresses] = useState({});  // Estado para manejar el progreso individual de cada canción
    const [isPlaying, setIsPlaying] = useState(false);        
    const [audioInstance, setAudioInstance] = useState(null); // Instancia del audio
    const [currentAudio, setCurrentAudio] = useState(null); // Estado para la canción actual
    const [volumes, setVolumes] = useState({}); // Estado para manejar el volumen individual de cada canción
    const [audioDurations, setAudioDurations] = useState({}); // Estado para manejar la duración de cada canción

    // Función para manejar el cambio de volumen individual
    const handleVolumeChange = (id, newValue) => {
        const newVolume = newValue / 100;  // Convertir el valor del slider (0-100) a escala de volumen (0-1)

        setVolumes((prevVolumes) => ({
            ...prevVolumes,
            [id]: newVolume,  // Actualizar el volumen de la canción individual
        }));

        if (currentAudio === id && audioInstance) {
            audioInstance.volume = newVolume; // Aplicar el volumen al audio que está reproduciéndose
        }
    };

    const handleProgressChange = (id, newValue) => {
        setProgresses((prevProgresses) => ({
            ...prevProgresses,
            [id]: newValue,  // Actualizar el progreso de la canción individual
        }));

        if (audioInstance && currentAudio === id) {
            audioInstance.currentTime = (newValue / 100) * audioInstance.duration;  // Cambiar la posición del audio actual
        }
    };

    // Función para obtener la lista de audios desde Firestore
    useEffect(() => {
        const fetchAudios = async () => {
            try {
                const audiosCollectionRef = collection(db, "milistacancionescalocho");
                const audiosSnapshot = await getDocs(audiosCollectionRef);
                const audiosList = audiosSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setAudios(audiosList);
            } catch (error) {
                console.error("Error obteniendo audios:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAudios();
    }, []);

    if (loading) {
        return <CircularProgress />;
    }

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    // Función para manejar la reproducción y pausa
    const handlePlayPause = (audioUrl, id) => {
        try {
            if (audioInstance) {
                audioInstance.pause(); // Pausar la canción actual
            }

            if (currentAudio === id && isPlaying) {
                setIsPlaying(false);
            } else {
                const newAudio = new Audio(audioUrl);

                // Aplicar el volumen individual o un valor por defecto de 0.3
                const volumeToApply = volumes[id] !== undefined ? volumes[id] : 0.3;
                newAudio.volume = volumeToApply;

                // Guardar la duración de la canción cuando se carguen los metadatos
                newAudio.addEventListener('loadedmetadata', () => {
                    setAudioDurations((prevDurations) => ({
                        ...prevDurations,
                        [id]: newAudio.duration, // Guardar la duración de la canción
                    }));
                });

                // Actualizar el progreso conforme se reproduce la canción
                newAudio.addEventListener('timeupdate', () => {
                    const currentProgress = (newAudio.currentTime / newAudio.duration) * 100;
                    setProgresses((prevProgresses) => ({
                        ...prevProgresses,
                        [id]: currentProgress,  // Solo actualizar el progreso de la canción actual
                    }));
                });

                newAudio.play();
                setAudioInstance(newAudio);
                setCurrentAudio(id); // Identificar la canción actual
                setIsPlaying(true);
            }
        } catch (error) {
            console.error("Error al pausar o iniciar la canción:", error);
            alert("Hubo un problema al intentar reproducir la canción.");
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: 600 },  // Ocupa el 100% en móviles, límite de 600px en pantallas más grandes
                margin: 'auto',
                padding: { xs: 1, sm: 2 },
                boxShadow: 3,
                borderRadius: 2,
            }}
        >
            {audios.length === 0 ? (
                <Typography variant="body1" align="center">
                    No hay audios subidos aún.
                </Typography>
            ) : (
                <List
                    sx={{
                        maxHeight: { xs: 300, sm: 400 }, // Ajusta la altura máxima (puedes cambiar el valor según tu diseño)
                        overflowY: 'auto',  // Habilitar scroll vertical cuando se exceda la altura máxima
                        paddingRight: 2,  // Para evitar que el scroll tape el contenido
                        '&::-webkit-scrollbar': {
                            width: '6px',  // Anchura más pequeña para la barra de scroll
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(93, 3, 3, 0.7)',  // Color personalizado para el thumb
                            borderRadius: '10px',  // Bordes redondeados
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',  // Color para el track
                        }
                    }}
                >
                    {audios.map((audio) => (
                        <ListItem
                            key={audio.id}
                            sx={{
                                flexDirection: { xs: 'row', md: 'row'},  // Cambia a columna en móviles para que el contenido fluya mejor
                                padding: { xs: 1, sm: 2 },
                                gap: { xs: 1, sm: 2 },  // Espacio entre los elementos
                                alignItems: 'center',
                                marginBottom: { xs: 1, sm: 2 },
                                borderRadius: 2,
                                border: '0.5px solid #f9f9f9',
                            }}
                        >
                            <Box sx={{width: { xs: '70%', sm: '85%', md: '80%', lg: '70%' }, alignItems: 'center', display: 'flex', flexDirection: 'column'}}>
                                <ListItemAvatar>
                                    <Avatar src={audio.thumbnailURL} alt={audio.name} variant="square" sx={{
                                        width: { xs: 100, sm: 80 },  // Reduce en pantallas pequeñas
                                        height: { xs: 60, sm: 80 },
                                    }}/>
                                </ListItemAvatar>
                                <ListItemText
                                    sx={{ 
                                        textAlign: { xs: 'center', sm: 'left' },
                                        fontSize: { xs: '0.6rem', sm: '1rem' },  // Tamaño de texto adaptable
                                        marginTop: { xs: 1, sm: 0 },  // Espacio adicional en móviles
                                    }}
                                    primary={audio.name}
                                    secondary={<span style={{ color:'#f9f9f9', fontSize: { xs: '0.4rem', sm: '1rem' }, textAlign: 'center' }}>Autor: {audio.author}</span>}
                                />
                            </Box>

                            {/* Controles del reproductor de música */}
                            <Box
                                sx={{
                                    width: { xs: '70%', sm: '85%', md: '80%', lg: '70%' },
                                    maxWidth: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{display: 'flex', flexDirection: 'row', width: { xs: '100%', sm: 150 },}}>
                                    {/* Icono de volumen y slider */}
                                    <VolumeUpIcon sx={{ mr: { xs: 1, sm: 2 } }} />
                                    <Slider
                                        value={volumes[audio.id] !== undefined ? volumes[audio.id] * 100 : 30}  // Valor de volumen individual
                                        onChange={(event, newValue) => handleVolumeChange(audio.id, newValue)}  // Manejar el cambio de volumen individual
                                        aria-labelledby="volume-slider"
                                        sx={{
                                            color: '#5d0303',
                                            width: { xs: '80%', sm: 150 },
                                        }}
                                    />
                                </Box>

                                {/* Barra de progreso de la canción */}
                                <Box sx={{display: 'flex', flexDirection: 'row', width: { xs: '80%', sm: 150 }, }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxWidth: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.9rem' }}>
                                            {formatTime(audioInstance && currentAudio === audio.id ? audioInstance.currentTime : 0)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.9rem' }}>
                                            {formatTime(audioDurations[audio.id] || 0)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Slider de progreso */}
                                <Slider
                                    value={progresses[audio.id] !== undefined ? progresses[audio.id] : 0}  // Valor de progreso individual
                                    onChange={(event, newValue) => handleProgressChange(audio.id, newValue)}  // Manejar el cambio de progreso individual
                                    aria-labelledby="progress-slider"
                                    sx={{
                                        color: '#5d0303',
                                        mx: 2,
                                        flex: 1,
                                        width: { xs: '80%', sm: 150 },
                                    }}
                                />
                            </Box>

                            <Box sx={{
                                display: 'flex', 
                                gap: '4px',
                                justifyContent: 'space-between',
                            }}>
                                <IconButton
                                    aria-label="play/pause"
                                    sx={{  color: "#fff", fontSize: { xs: '1.0rem', sm: '1.5rem' } }}
                                    onClick={() => handlePlayPause(audio.audioURL, audio.id)}  // Pasar el ID para identificar la canción
                                >
                                    {isPlaying && currentAudio === audio.id ? <PauseIcon /> : <PlayArrowIcon />}
                                </IconButton>
                            </Box>

                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default ListaCancionesWeb;